import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userService } from '../services/userService';

/**
 * Async thunk to fetch ALL user permissions at login (once)
 */
export const fetchUserPermissionsThunk = createAsyncThunk(
  'user/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getUserPermissions();
      return response.data; // { permissions: [list of all user permissions] }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch all internal users for dropdowns
 */
export const fetchAllInternalUsersThunk = createAsyncThunk(
  'user/fetchAllInternal',
  async (orgId, { rejectWithValue }) => {
    try {
      const response = await userService.getAllInternalUsersREST(orgId);
      return response.data; // List of users
    } catch (error) {
      console.error('Failed to fetch all internal users:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchClaimSavedFiltersThunk = createAsyncThunk(
  'user/fetchClaimSavedFilters',
  async (userIdArg, { getState, rejectWithValue }) => {
    try {
      const userId = userIdArg || getState().user.userData?.id;
      if (!userId) return [];
      const response = await userService.getClaimSavedFilters(userId);
      return Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch claim saved filters:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const saveClaimSavedFiltersThunk = createAsyncThunk(
  'user/saveClaimSavedFilters',
  async ({ userId: userIdArg, savedFilters }, { getState, rejectWithValue }) => {
    try {
      const userId = userIdArg || getState().user.userData?.id;
      if (!userId) throw new Error('User not found');
      const response = await userService.saveClaimSavedFilters(userId, savedFilters || []);
      return Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to save claim saved filters:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSmsNotificationPreferenceThunk = createAsyncThunk(
  'user/updateSmsNotificationPreference',
  async ({ userId: userIdArg, enabled }, { getState, rejectWithValue }) => {
    try {
      const userId = userIdArg || getState().user.userData?.id;
      if (!userId) throw new Error('User not found');
      const response = await userService.updateSmsNotificationPreference(userId, enabled);
      return response?.data || { enabled }; 
    } catch (error) {
      console.error('Failed to update SMS notification preference:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateScannerProductKeyThunk = createAsyncThunk(
  'user/updateScannerProductKey',
  async ({ userId: userIdArg, productKey }, { getState, rejectWithValue }) => {
    try {
      const userId = userIdArg || getState().user.userData?.id;
      if (!userId) throw new Error('User not found');
      const response = await userService.updateScannerProductKey(userId, productKey);
      return response?.data || { productKey }; 
    } catch (error) {
      console.error('Failed to update scanner product key:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  userData: null,
  userPermissions: [], // All user's permissions fetched once
  allUsers: [], // All internal users for dropdowns
  claimSavedFilters: [],
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.userData = action.payload.user;
      state.claimSavedFilters = action.payload.user?.preferences?.dashboard?.claimFilters?.saved || [];
      state.error = null;
    },
    setPermissions: (state, action) => {
      state.userPermissions = action.payload;
    },
    clearUser: (state) => {
      state.userData = null;
      state.userPermissions = [];
      state.claimSavedFilters = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setClaimSavedFiltersLocal: (state, action) => {
      state.claimSavedFilters = Array.isArray(action.payload) ? action.payload : [];
      if (state.userData) {
        state.userData = {
          ...state.userData,
          preferences: {
            ...(state.userData.preferences || {}),
            dashboard: {
              ...(state.userData.preferences?.dashboard || {}),
              claimFilters: {
                saved: state.claimSavedFilters,
              },
            },
          },
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPermissionsThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserPermissionsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.userPermissions = action.payload.permissions || [];
      })
      .addCase(fetchUserPermissionsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.userPermissions = [];
      })
      .addCase(fetchAllInternalUsersThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllInternalUsersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.allUsers = Array.isArray(action.payload) ? action.payload : (action.payload?.users || []);
      })
      .addCase(fetchAllInternalUsersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchClaimSavedFiltersThunk.fulfilled, (state, action) => {
        const saved = Array.isArray(action.payload) ? action.payload : [];
        state.claimSavedFilters = saved;
        if (state.userData) {
          state.userData = {
            ...state.userData,
            preferences: {
              ...(state.userData.preferences || {}),
              dashboard: {
                ...(state.userData.preferences?.dashboard || {}),
                claimFilters: {
                  saved,
                },
              },
            },
          };
        }
      })
      .addCase(saveClaimSavedFiltersThunk.fulfilled, (state, action) => {
        const saved = Array.isArray(action.payload) ? action.payload : [];
        state.claimSavedFilters = saved;
        if (state.userData) {
          state.userData = {
            ...state.userData,
            preferences: {
              ...(state.userData.preferences || {}),
              dashboard: {
                ...(state.userData.preferences?.dashboard || {}),
                claimFilters: {
                  saved,
                },
              },
            },
          };
        }
      })
      .addCase(updateSmsNotificationPreferenceThunk.fulfilled, (state, action) => {
        if (state.userData) {
          state.userData = {
            ...state.userData,
            preferences: {
              ...(state.userData.preferences || {}),
              smsNotificationsEnabled: action.payload.enabled
            }
          };
        }
      })
      .addCase(updateScannerProductKeyThunk.fulfilled, (state, action) => {
        if (state.userData) {
          state.userData = {
            ...state.userData,
            preferences: {
              ...(state.userData.preferences || {}),
              scannerProductKey: action.payload.productKey
            }
          };
          
          // Sync to localStorage
          const cachedUser = localStorage.getItem('currentUser');
          if (cachedUser) {
            try {
              const parsed = JSON.parse(cachedUser);
              parsed.preferences = {
                ...(parsed.preferences || {}),
                scannerProductKey: action.payload.productKey
              };
              localStorage.setItem('currentUser', JSON.stringify(parsed));
            } catch (e) {
              console.error('Failed to sync updated user to localStorage:', e);
            }
          }
        }
      });
  },
});

export const { setUser, setPermissions, clearUser, clearError, setClaimSavedFiltersLocal } = userSlice.actions;

/**
 * Selector: Check if user has required permissions
 */
export const selectHasPermission = (state, requiredPermissions) => {
  const userPerms = state.user.userPermissions || [];
  const userDataPerms = state.user.userData?.permissions || [];

  const checkList = (list) => {
    if (!Array.isArray(requiredPermissions)) {
      return list.includes(requiredPermissions);
    }
    // If array: check if user has ANY of the permissions (OR logic)
    return requiredPermissions.some(p => list.includes(p));
  };

  return checkList(userPerms) || checkList(userDataPerms);
};

export default userSlice.reducer;
